import { importPublishedRulebook, NotificationType, useTranslation } from "@next-platform/client-common-core";
import resolveRulebookListCardStatus from "@next-platform/client-common-core/rulebook/cards/resolveRulebookListCardStatus";
import { RulebookListCardStatus } from "@next-platform/client-common-core/rulebook/cards/RulebookListCardStatus";
import { type RulebookDraft } from "@next-platform/client-common-core/rulebook/editor/rulebookDraftStore";
import useRulebookDrafts from "@next-platform/client-common-core/rulebook/editor/useRulebookDrafts";
import { type DropDownMenuItem, IconVariants } from "@next-platform/client-common-spicy-ui";
import { RulebookStatus, type RulebookSummary, validateRulebook } from "@next-platform/packages-ts-types";
import type { TFunction } from "i18next";
import { useCallback, useMemo, useRef } from "react";

import useCloneRulebook from "@/hooks/useCloneRulebook";
import useToast from "@/hooks/useToast";

import downloadRulebookExport from "../utils/downloadRulebookExport";
import useRulebookDeleteConfirmation from "./useRulebookDeleteConfirmation";
import useSavedRulebookDelete from "./useSavedRulebookDelete";

type UseMyRulebookCardActionsOptions = {
  summary?: RulebookSummary;
  draft?: RulebookDraft;
  onOpen: (draftId: string) => void;
  onReloadSaved?: () => void;
};

const savedDeleteLabel = (draft: RulebookDraft | undefined, isPublished: boolean, translate: TFunction): string => {
  if (!draft) {
    return translate("myCreation:card.actions.remove");
  }
  return isPublished ? translate("rulebookEditor:card.deleteRulebook") : translate("rulebookEditor:card.delete");
};

type MenuActionOptions = {
  summary?: RulebookSummary;
  draft?: RulebookDraft;
  isCloning: boolean;
  translate: TFunction;
  onOpen: () => void;
  onClone: () => void;
  onExport: () => void;
  onDuplicate: () => void;
  isPublished: boolean;
  onDeleteDraft: () => void;
  onDeleteSaved: () => void;
};

const buildMenuActions = ({
  summary,
  draft,
  isCloning,
  translate,
  onOpen,
  onClone,
  onExport,
  onDuplicate,
  isPublished,
  onDeleteDraft,
  onDeleteSaved,
}: MenuActionOptions): DropDownMenuItem[] => {
  const isModerationBlocked = summary?.status === RulebookStatus.IN_MODERATION || summary?.status === RulebookStatus.MODERATION_FAILED;
  const cardStatus = resolveRulebookListCardStatus({ summary });
  const isCloneBlocked =
    isModerationBlocked || cardStatus === RulebookListCardStatus.NEEDS_CHANGES || cardStatus === RulebookListCardStatus.IN_REVIEW;
  const cloneAction: DropDownMenuItem[] =
    summary && !isCloneBlocked
      ? [{ label: translate("myCreation:card.actions.clone"), icon: IconVariants.COPY, isDisabled: isCloning, onPress: onClone }]
      : [];
  const draftActions: DropDownMenuItem[] = draft
    ? [
        { label: translate("rulebookEditor:card.export"), icon: IconVariants.DOWNLOAD, onPress: onExport },
        { label: translate("rulebookEditor:card.duplicate"), icon: IconVariants.COPY, onPress: onDuplicate },
      ]
    : [];
  return [
    { label: translate("myCreation:card.actions.edit"), icon: IconVariants.EDIT, onPress: onOpen },
    ...cloneAction,
    ...draftActions,
    {
      label: savedDeleteLabel(draft, isPublished, translate),
      icon: IconVariants.TRASH,
      onPress: draft ? onDeleteDraft : onDeleteSaved,
      className: "text-red-9",
    },
  ];
};

/** Menu handlers and delete-modal state for one My Rulebooks card. */
const useMyRulebookCardActions = ({ summary, draft, onOpen, onReloadSaved }: UseMyRulebookCardActionsOptions) => {
  const { t } = useTranslation();
  const refIsImporting = useRef(false);
  const { displayToast } = useToast();
  const { duplicateDraft } = useRulebookDrafts();
  const { clone, isCloning } = useCloneRulebook();
  const savedDelete = useSavedRulebookDelete({ ...(summary ? { summary } : {}), onReloadSaved });
  const draftId = draft?.definition.id ?? "";
  const deletion = useRulebookDeleteConfirmation({ draftId, name: draft?.definition.name ?? summary?.name ?? "", onDeleted: onReloadSaved });

  const handleOpen = useCallback((): void => {
    if (draft) {
      onOpen(draft.definition.id);
      return;
    }
    if (!summary || refIsImporting.current) {
      return;
    }
    refIsImporting.current = true;
    importPublishedRulebook({ rulebookId: summary.id })
      .then((result) => {
        if (result.status !== "imported") {
          displayToast({ type: NotificationType.error, message: t("rulebookEditor:savedCard.openFailed") });
          return;
        }
        onReloadSaved?.();
        onOpen(result.draftId);
      })
      .catch(() => displayToast({ type: NotificationType.error, message: t("rulebookEditor:savedCard.openFailed") }))
      .finally(() => {
        refIsImporting.current = false;
      });
  }, [summary, draft, displayToast, t, onReloadSaved, onOpen]);

  const handleClone = useCallback(() => {
    if (summary) {
      clone(summary.id);
    }
  }, [summary, clone]);

  const handleExport = useCallback(() => {
    if (!draft) {
      return;
    }
    const validation = validateRulebook(draft.definition);
    if (!validation.valid) {
      displayToast({ type: NotificationType.error, message: t("rulebookEditor:card.exportInvalid") });
      return;
    }
    downloadRulebookExport({ definition: draft.definition });
  }, [draft, displayToast, t]);

  const handleDuplicate = useCallback(() => {
    if (draft) {
      duplicateDraft(draft.definition.id);
    }
  }, [draft, duplicateDraft]);

  const menuActions = useMemo<DropDownMenuItem[]>(
    () =>
      buildMenuActions({
        ...(summary ? { summary } : {}),
        ...(draft ? { draft } : {}),
        isCloning,
        translate: t,
        onOpen: handleOpen,
        onClone: handleClone,
        onExport: handleExport,
        onDuplicate: handleDuplicate,
        isPublished: deletion.isPublished,
        onDeleteDraft: deletion.open,
        onDeleteSaved: savedDelete.open,
      }),
    [summary, draft, isCloning, t, handleOpen, handleClone, handleExport, handleDuplicate, deletion.isPublished, deletion.open, savedDelete.open],
  );

  return {
    menuActions,
    open: handleOpen,
    draftDeletion: deletion,
    savedDelete,
  };
};

export default useMyRulebookCardActions;
